import { supabase } from "@/integrations/supabase/client";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdminRow {
  person_id: number;
  first_name: string;
  last_name: string | null;
  primary_email: string | null;
  granted_at: string;
}

export interface HostAssignment {
  person_id: number;
  first_name: string;
  last_name: string | null;
  primary_email: string | null;
  created_at: string;
}

export interface LocationHosts {
  location_id: number;
  city: string;
  country: string | null;
  hosts: HostAssignment[];
}

/** Shape of the embedded person we get back from PostgREST joins below. */
type EmbeddedPerson = {
  first_name: string;
  last_name: string | null;
  person_email: { email: string; is_primary: boolean }[] | null;
} | null;

/** Prefer the row marked is_primary, fall back to the first email, then null. */
function pickPrimaryEmail(
  emails: { email: string; is_primary: boolean }[] | null | undefined,
): string | null {
  const list = emails ?? [];
  return (list.find((e) => e.is_primary) ?? list[0])?.email ?? null;
}

function fullNameKey(p: { first_name: string; last_name: string | null }): string {
  return `${p.first_name} ${p.last_name ?? ""}`;
}

// ─── Admins ─────────────────────────────────────────────────────────────────

/** All persons holding the admin role, sorted by name. */
export async function listAdmins(): Promise<AdminRow[]> {
  const { data, error } = await supabase
    .from("person_role")
    .select(
      "person_id, created_at, person:person_id(first_name, last_name, person_email(email, is_primary))",
    )
    .eq("role", "admin")
    .order("created_at", { ascending: true });
  if (error) throw error;

  type Row = { person_id: number; created_at: string; person: EmbeddedPerson };

  return ((data ?? []) as unknown as Row[])
    .map<AdminRow>((row) => ({
      person_id: row.person_id,
      first_name: row.person?.first_name ?? "",
      last_name: row.person?.last_name ?? null,
      primary_email: pickPrimaryEmail(row.person?.person_email),
      granted_at: row.created_at,
    }))
    .sort((a, b) => fullNameKey(a).localeCompare(fullNameKey(b)));
}

export async function grantAdmin(personId: number): Promise<void> {
  // Idempotent: PK is (person_id, role), so ignoreDuplicates makes re-grants a no-op.
  const { error } = await supabase
    .from("person_role")
    .upsert(
      { person_id: personId, role: "admin" },
      { onConflict: "person_id,role", ignoreDuplicates: true },
    );
  if (error) throw error;
}

export async function revokeAdmin(personId: number): Promise<void> {
  const { error } = await supabase
    .from("person_role")
    .delete()
    .eq("person_id", personId)
    .eq("role", "admin");
  if (error) throw error;
}

// ─── Hosts ──────────────────────────────────────────────────────────────────

/**
 * All location_host rows joined with person + location, grouped by location.
 * Locations with no hosts are NOT returned here — callers that need the
 * exhaustive list of locations should fetch locations separately and merge.
 */
export async function listHostsByLocation(): Promise<LocationHosts[]> {
  const { data, error } = await supabase
    .from("location_host")
    .select(
      "location_id, person_id, created_at, " +
        "location:location_id(city, country), " +
        "person:person_id(first_name, last_name, person_email(email, is_primary))",
    )
    .order("created_at", { ascending: true });
  if (error) throw error;

  type Row = {
    location_id: number;
    person_id: number;
    created_at: string;
    location: { city: string; country: string | null } | null;
    person: EmbeddedPerson;
  };

  const byLocation = new Map<number, LocationHosts>();
  for (const row of (data ?? []) as unknown as Row[]) {
    const host: HostAssignment = {
      person_id: row.person_id,
      first_name: row.person?.first_name ?? "",
      last_name: row.person?.last_name ?? null,
      primary_email: pickPrimaryEmail(row.person?.person_email),
      created_at: row.created_at,
    };
    const existing = byLocation.get(row.location_id);
    if (existing) {
      existing.hosts.push(host);
    } else {
      byLocation.set(row.location_id, {
        location_id: row.location_id,
        city: row.location?.city ?? "",
        country: row.location?.country ?? null,
        hosts: [host],
      });
    }
  }
  return Array.from(byLocation.values());
}

export async function assignHost(personId: number, locationId: number): Promise<void> {
  // Idempotent: PK is (location_id, person_id), so ignoreDuplicates makes re-assigns a no-op.
  const { error } = await supabase
    .from("location_host")
    .upsert(
      { person_id: personId, location_id: locationId },
      { onConflict: "location_id,person_id", ignoreDuplicates: true },
    );
  if (error) throw error;
}

export async function unassignHost(personId: number, locationId: number): Promise<void> {
  const { error } = await supabase
    .from("location_host")
    .delete()
    .eq("person_id", personId)
    .eq("location_id", locationId);
  if (error) throw error;
}

// ─── Role RPC wrappers (used by AuthContext) ────────────────────────────────

/** location_ids where the signed-in user is a host. Empty for non-hosts. */
export async function fetchMyHostLocationIds(): Promise<number[]> {
  const { data, error } = await supabase.rpc("my_host_location_ids");
  if (error) throw error;
  return (data ?? []) as number[];
}

/** True iff the signed-in user holds the admin role. */
export async function fetchIsAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_admin");
  if (error) throw error;
  return !!data;
}